/**
 * @ares/tokens — Theme Token Loader
 *
 * Multi-Tenant + Multi-Mode Token-System für ARES Bio.OS Desktop.
 * Phase 3 Sprint A · 2026-04-26 · Stub-Mode (file-import).
 *
 * Usage:
 *   import { applyTheme, getTokens } from '@ares/tokens';
 *   applyTheme('ares', 'light');                  // Default
 *   applyTheme('salutem', 'light');               // Whitelabel-Tenant
 *   const tokens = getTokens('ares', 'dark');     // Read-only access
 */

import lightTokens from './light.json';
import darkTokens from './dark.json';
import medicTokens from './medic.json';
import salutemTokens from './salutem.json';

// ============================================================
// TYPES
// ============================================================

export type Tenant = 'ares' | 'salutem' | (string & {}); // Future-Tenants
export type Mode = 'light' | 'dark' | 'medic';
/**
 * Hinweis: `medic` ist ein THIRD-Mode neben Light/Dark — wählbar im Settings.
 * Medic-Mode aktiviert Klinik-Look (Teal+Sage) UND Klinik-Voice (i18n switch).
 * Salutem-Tenant nutzt medic als forced-default.
 */

export interface TokenValue {
  value: string | number;
  comment?: string;
  [key: string]: unknown; // typography hat extra fields wie weight, letterSpacing
}

/**
 * Token-leaf or string — JSON files keep `comment` strings alongside
 * `{ value }` leaves at the same Record level, so the value type is a
 * union. Token-walkers must check `typeof v === 'object'` before reading
 * `v.value`.
 */
export type TokenLeaf = TokenValue | string;

export interface ColorTokens {
  background?: TokenValue;
  surface?: TokenValue;
  'surface-2'?: TokenValue;
  border?: TokenValue;
  'border-strong'?: TokenValue;
  'soft-gray'?: TokenValue;
  text?: {
    primary?: TokenValue;
    dim?: TokenValue;
    subtle?: TokenValue;
    inverse?: TokenValue;
  };
  accent?: TokenValue;
  primary?: TokenValue;
  warm?: TokenValue;
  warn?: TokenValue;
  alert?: TokenValue;
  score?: Record<string, TokenLeaf>;
  'domain-tint'?: Record<string, TokenLeaf>;
  glow?: Record<string, TokenLeaf>;
  'brand-gradient'?: TokenValue;
  [key: string]: unknown;
}

export interface ThemeTokens {
  $schema?: string;
  name: string;
  description?: string;
  version: string;
  tenant: Tenant;
  mode: Mode;
  extends?: string;
  color?: ColorTokens;
  glass?: Record<string, TokenLeaf>;
  radius?: Record<string, TokenLeaf>;
  spacing?: Record<string, TokenLeaf>;
  typography?: Record<string, unknown>;
  motion?: Record<string, unknown>;
  z?: Record<string, TokenLeaf>;
  branding?: Record<string, unknown>;
  compliance?: Record<string, unknown>;
  'ui-overrides'?: Record<string, unknown>;
  'tenant-info'?: Record<string, unknown>;
  [key: string]: unknown;
}

// ============================================================
// REGISTRY
// ============================================================

interface RegistryEntry {
  tenant: Tenant;
  mode: Mode;
  tokens: ThemeTokens;
}

const TOKEN_REGISTRY: RegistryEntry[] = [
  // ARES (Konsumer-Tenant) — User wählt Theme im Settings
  { tenant: 'ares', mode: 'light', tokens: lightTokens as ThemeTokens },
  { tenant: 'ares', mode: 'dark', tokens: darkTokens as ThemeTokens },
  { tenant: 'ares', mode: 'medic', tokens: medicTokens as ThemeTokens },

  // Salutem-Whitelabel (Klinik-Tenant) — forced medic + Salutem-Branding-Override
  { tenant: 'salutem', mode: 'medic', tokens: salutemTokens as ThemeTokens },

  // Future: { tenant: 'enterprise-XYZ', mode: 'medic', tokens: enterpriseXYZTokens },
];

/**
 * Returns the i18n-locale that should be auto-activated when a mode is selected.
 * Medic-Mode → de-medic (oder en-medic je nach Browser)
 * Light/Dark → kein auto-switch (User-Locale bleibt)
 */
export function getCoupledLocale(mode: Mode, tenant: Tenant = 'ares', browserLanguage?: string): string | null {
  if (mode !== 'medic') return null;
  // Salutem-Tenant: forced de-salutem
  if (tenant === 'salutem') return 'de-salutem';
  // ARES-User der medic gewählt hat: nimm de-medic oder en-medic je nach Browser
  const browser = browserLanguage ?? (typeof navigator !== 'undefined' ? navigator.language : 'en');
  return browser.startsWith('de') ? 'de-medic' : 'en-medic';
}

/**
 * Register a custom Tenant-Token-Set at runtime.
 * Useful für Enterprise-Whitelabel ohne Code-Deploy.
 */
export function registerTenant(tenant: Tenant, mode: Mode, tokens: ThemeTokens): void {
  // Replace if exists
  const idx = TOKEN_REGISTRY.findIndex(e => e.tenant === tenant && e.mode === mode);
  if (idx >= 0) {
    TOKEN_REGISTRY[idx] = { tenant, mode, tokens };
  } else {
    TOKEN_REGISTRY.push({ tenant, mode, tokens });
  }
}

// ============================================================
// LOOKUP
// ============================================================

/**
 * Returns merged tokens for a (tenant, mode) combo.
 * Cascade: base-mode (light/dark for ARES) → tenant-override (salutem.json extends light.json) → user-overrides (later).
 */
export function getTokens(tenant: Tenant = 'ares', mode: Mode = 'light'): ThemeTokens {
  // 1. Try direct lookup
  const direct = TOKEN_REGISTRY.find(e => e.tenant === tenant && e.mode === mode);
  if (direct) {
    // If the tokens have an `extends` field, merge with the parent
    if (direct.tokens.extends) {
      const parent = getTokens('ares', direct.tokens.extends as Mode);
      return mergeTokens(parent, direct.tokens);
    }
    return direct.tokens;
  }

  // 2. Fallback: tenant exists but not for this mode? Try light.
  if (mode === 'dark') {
    const fallback = TOKEN_REGISTRY.find(e => e.tenant === tenant && e.mode === 'light');
    if (fallback) {
      console.warn(`[ares/tokens] No dark-mode für tenant "${tenant}", falling back to light.`);
      return fallback.tokens;
    }
  }

  // 3. Last resort: ARES light
  console.warn(`[ares/tokens] No tokens für (${tenant}, ${mode}), using ares/light.`);
  const fallback = TOKEN_REGISTRY[0];
  if (!fallback) throw new Error('[ares/tokens] empty registry — no fallback available');
  return fallback.tokens;
}

/**
 * Deep-merge two token objects. Child wins on conflict.
 * Token structure is mostly { value, comment } — child keys replace parent keys.
 */
function mergeTokens(parent: ThemeTokens, child: Partial<ThemeTokens>): ThemeTokens {
  const merged = JSON.parse(JSON.stringify(parent)); // deep clone

  function recursiveMerge(target: any, source: any): void {
    for (const key in source) {
      if (key === 'extends' || key === '$schema') continue;
      if (
        typeof source[key] === 'object' &&
        source[key] !== null &&
        !Array.isArray(source[key]) &&
        !('value' in source[key])  // Token-Leaf has `value`, intermediate objects don't
      ) {
        if (!target[key]) target[key] = {};
        recursiveMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }

  recursiveMerge(merged, child);
  merged.tenant = child.tenant ?? parent.tenant;
  merged.mode = child.mode ?? parent.mode;
  return merged;
}

// ============================================================
// DOM-APPLICATION
// ============================================================

/**
 * Apply tokens to `document.documentElement` as CSS-Custom-Properties.
 * Re-applies on tenant-switch or mode-toggle.
 */
export function applyTheme(tenant: Tenant = 'ares', mode: Mode = 'light'): void {
  if (typeof document === 'undefined') return; // SSR-Guard
  const tokens = getTokens(tenant, mode);
  const root = document.documentElement;

  // Set tenant + mode dataset (für CSS-Selektoren wie [data-tenant="salutem"])
  root.dataset.tenant = tenant;
  root.dataset.mode = mode;
  root.classList.toggle('dark', mode === 'dark');

  // Flatten tokens into CSS-Custom-Properties
  const cssVars = flattenToCssVars(tokens);
  for (const [name, value] of Object.entries(cssVars)) {
    root.style.setProperty(name, String(value));
  }
}

/**
 * Convert nested token tree → flat CSS-Custom-Property map.
 * { color: { accent: { value: '#34C759' } } } → { '--color-accent': '#34C759' }
 */
function flattenToCssVars(tokens: ThemeTokens): Record<string, string | number> {
  const result: Record<string, string | number> = {};

  function walk(node: unknown, path: string[]): void {
    if (node && typeof node === 'object' && !Array.isArray(node) && 'value' in node) {
      const value = (node as TokenValue).value;
      const cssName = '--' + path.join('-');
      result[cssName] = typeof value === 'number' ? value : String(value);
      return;
    }
    if (node && typeof node === 'object' && !Array.isArray(node)) {
      for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
        if (
          ['$schema', 'name', 'description', 'version', 'tenant', 'mode', 'extends',
            'comment', 'tenant-info', 'branding', 'compliance', 'ui-overrides'].includes(key)
        ) {
          continue;
        }
        walk(child, [...path, key]);
      }
    }
  }

  for (const topKey of ['color', 'glass', 'radius', 'spacing', 'z'] as const) {
    const sub = tokens[topKey];
    if (sub) walk(sub, [topKey]);
  }

  return result;
}

// ============================================================
// CONVENIENCE
// ============================================================

/** Returns list of available tenants. */
export function listTenants(): Tenant[] {
  return Array.from(new Set(TOKEN_REGISTRY.map(e => e.tenant)));
}

/** Returns list of available modes for a tenant. */
export function listModes(tenant: Tenant): Mode[] {
  return TOKEN_REGISTRY.filter(e => e.tenant === tenant).map(e => e.mode);
}

/** Returns tenant-info metadata (legal-name, address, etc.) — useful für Footer/Impressum. */
export function getTenantInfo(tenant: Tenant): Record<string, unknown> | undefined {
  const entry = TOKEN_REGISTRY.find(e => e.tenant === tenant);
  return entry?.tokens['tenant-info'];
}

/** Returns ui-overrides für ein Tenant — z.B. ob Discord/RPG/etc. enabled ist. */
export function getUiOverrides(tenant: Tenant): Record<string, unknown> {
  const entry = TOKEN_REGISTRY.find(e => e.tenant === tenant);
  return (entry?.tokens['ui-overrides'] ?? {}) as Record<string, unknown>;
}

/** Returns compliance-config (MDR-Disclaimer, Audit-Log-Level) für ein Tenant. */
export function getComplianceConfig(tenant: Tenant): Record<string, unknown> {
  const entry = TOKEN_REGISTRY.find(e => e.tenant === tenant);
  return (entry?.tokens['compliance'] ?? {}) as Record<string, unknown>;
}

// ============================================================
// EXPORTS
// ============================================================

export const tokens = {
  light: lightTokens as ThemeTokens,
  dark: darkTokens as ThemeTokens,
  medic: medicTokens as ThemeTokens,
  salutem: salutemTokens as ThemeTokens,
};

/** List of available themes for User-Settings-Picker (für ARES-Tenant). */
export const THEME_PICKER_OPTIONS: Array<{ mode: Mode; label: string; description: string }> = [
  { mode: 'light', label: 'Light', description: 'Default — bright, clean, Apple-DNA' },
  { mode: 'dark', label: 'Dark', description: 'Cockpit — focused, low-light, glow accents' },
  { mode: 'medic', label: 'Medic', description: 'Clinical — healing teal/sage, warm-medical voice' },
];

export default {
  applyTheme,
  getTokens,
  getCoupledLocale,
  registerTenant,
  listTenants,
  listModes,
  getTenantInfo,
  getUiOverrides,
  getComplianceConfig,
  tokens,
  THEME_PICKER_OPTIONS,
};
